import { useSearchParams, useNavigate } from 'react-router-dom';

import { FormattedMessage } from '@zenbusiness/application-commons-intl';
import { ErrorBoundary } from '@zenbusiness/application-commons-logging';
import Box from '@zenbusiness/zui/Box';
import Button from '@zenbusiness/zui/Button';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import { dashboard, invoicePayment } from '#client/constants/routes';

import InvoiceCard from './components/InvoiceCard';
import PaymentAlert from './components/PaymentAlert';
import PaymentReturnHeader from './components/PaymentReturnHeader';
import WhatsNext from './components/WhatsNext';
import {
  PAYMENT_STATUS,
  SUCCESS_ILLUSTRATION_SRC,
  ERROR_ILLUSTRATION_SRC
} from './constants';
import { PaymentReturnPageRoot, PaymentReturnContent } from './PaymentReturn.style';

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

function formatAmount(amountInCents) {
  const dollars = Number(amountInCents) / 100;
  if (Number.isNaN(dollars)) return currencyFormatter.format(0);
  return currencyFormatter.format(dollars);
}

export default function PaymentReturn() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const status = searchParams.get('status') || PAYMENT_STATUS.FAILURE;
  const companyName = searchParams.get('companyName') || '';
  const amount = searchParams.get('amount') || '0';
  const unpaidCount = Number(searchParams.get('unpaidCount') || 0);

  const isSuccess = status === PAYMENT_STATUS.SUCCESS;
  const displayAmount = formatAmount(amount);
  const illustrationSrc = isSuccess ? SUCCESS_ILLUSTRATION_SRC : ERROR_ILLUSTRATION_SRC;

  const handleCtaClick = () => {
    if (isSuccess) {
      navigate(dashboard);
    } else {
      navigate(invoicePayment);
    }
  };

  return (
    <ErrorBoundary id="src/pages/PaymentReturn#PaymentReturn">
      <PaymentReturnPageRoot>
        <PaymentReturnHeader />
        <PaymentReturnContent>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
            <Stack flex={1} gap={3}>
              {companyName && (
                <Text size="sm" color="secondary" uppercase>
                  {companyName}
                </Text>
              )}
              <Text size="2xl" weight="bold" family="secondary">
                <FormattedMessage
                  id={isSuccess
                    ? 'dashboard.paymentReturn.title.success'
                    : 'dashboard.paymentReturn.title.failure'}
                />
              </Text>
              <Text size="md" color="secondary">
                <FormattedMessage
                  id={isSuccess
                    ? 'dashboard.paymentReturn.subtitle.success'
                    : 'dashboard.paymentReturn.subtitle.failure'}
                />
              </Text>

              {!isSuccess && <PaymentAlert />}

              <InvoiceCard isSuccess={isSuccess} amount={displayAmount} />

              <WhatsNext isSuccess={isSuccess} unpaidCount={unpaidCount} />

              <Button
                variant="contained"
                size="large"
                onClick={handleCtaClick}
                sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
              >
                <FormattedMessage
                  id={isSuccess
                    ? 'dashboard.paymentReturn.cta.success'
                    : 'dashboard.paymentReturn.cta.failure'}
                />
              </Button>
            </Stack>

            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1
              }}
            >
              <SharedAsset
                alt=""
                src={illustrationSrc}
                width={200}
                height={200}
              />
            </Box>
          </Box>
        </PaymentReturnContent>
      </PaymentReturnPageRoot>
    </ErrorBoundary>
  );
}
